import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { Lock, Mail, AlertCircle, Loader2, CheckCircle2, MapPin, DollarSign, Zap } from "lucide-react";
import { ForgotPasswordModal, ForgotEmailModal } from "../components/AuthRecoveryModals";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ScoutLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [showFP, setShowFP] = useState(false);
  const [showFE, setShowFE] = useState(false);

  useEffect(() => {
    // Bail out if a password recovery is in progress — never auto-open dashboard.
    try {
      if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") return;
    } catch { /* ignore */ }
    supabase.auth.getSession().then(async ({ data }) => {
      if (data?.session) await linkAndGo(data.session.access_token, null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function linkAndGo(token, profile) {
    try {
      const r = await fetch(`${API}/api/scouts/auth/link`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
        body: JSON.stringify(profile || {}),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        if (r.status === 400 && d.detail === "missing_profile") {
          setError(`Missing required fields: ${(d.fields || []).join(", ")}`);
          setMode("signup"); setLoading(false); return;
        }
        setInfo("Check your email to confirm your account, then sign in.");
        setAwaitingVerification(true); setLoading(false); return;
      }
    } catch { /* non-fatal */ }
    nav("/scouts/dashboard", { replace: true });
  }

  async function submit(e) {
    e.preventDefault(); setLoading(true); setError(""); setInfo("");
    try {
      if (mode === "signup") {
        const missing = [];
        if (!firstName.trim()) missing.push("First Name");
        if (!lastName.trim()) missing.push("Last Name");
        if (!phone.trim()) missing.push("Phone");
        if (!zip.trim()) missing.push("ZIP");
        if (!state.trim()) missing.push("State");
        if (missing.length) { setError(`Please provide: ${missing.join(", ")}`); setLoading(false); return; }
        const { data, error } = await supabase.auth.signUp({
          email, password,
          options: { data: { first_name: firstName.trim(), last_name: lastName.trim() } },
        });
        if (error) { setError(error.message); setLoading(false); return; }
        const profile = {
          first_name: firstName.trim(), last_name: lastName.trim(),
          phone: phone.trim(), zip: zip.trim(), state: state.trim().toUpperCase(),
        };
        if (data?.session) return linkAndGo(data.session.access_token, profile);
        try { sessionStorage.setItem("sb_scout_profile", JSON.stringify(profile)); } catch { /* ignore */ }
        setInfo("Signup received. Check your email to confirm, then sign in.");
        setAwaitingVerification(true); setMode("signin"); setLoading(false);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) { setError(error.message); setLoading(false); return; }
        let profile = null;
        try {
          const raw = sessionStorage.getItem("sb_scout_profile");
          if (raw) { profile = JSON.parse(raw); sessionStorage.removeItem("sb_scout_profile"); }
        } catch { /* ignore */ }
        return linkAndGo(data.session.access_token, profile);
      }
    } catch (e) { setError("Unexpected error"); setLoading(false); }
  }

  async function resendVerification() {
    if (!email) { setError("Enter your email above first"); return; }
    setError(""); setInfo("");
    const { error } = await supabase.auth.resend({ type: "signup", email });
    if (error) setError(error.message);
    else setInfo("Verification email re-sent. Check your inbox.");
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex flex-col">
      <Seo title="Scout Login" description="SpotBounty Scout portal" path="/scouts/login" noindex />
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/"><SBLogo size={20} /></Link>
          <Link to="/scouts" className="text-[12px] text-[#A1A1AA] hover:text-white">Back to Scouts</Link>
        </div>
      </header>

      <main className="flex-1 flex items-stretch">
        <div className="max-w-6xl mx-auto w-full grid lg:grid-cols-2 gap-0">
          {/* Left pitch panel (desktop only) */}
          <aside className="hidden lg:flex flex-col justify-center px-10 py-16 border-r border-[#2A2F34] relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-[.08]" style={{
              backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,193,7,.4) 0, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,193,7,.25) 0, transparent 45%)",
            }} />
            <div className="relative">
              <div className="sb-eyebrow">Scout Portal</div>
              <h1 className="mt-3 sb-headline text-[44px] leading-[1.05]">
                FIND VENDING<br /><span className="text-[#FFC107]">LOCATIONS.</span><br />EARN REWARDS.
              </h1>
              <p className="mt-5 text-[14px] text-[#A1A1AA] max-w-md">
                Submit real vending opportunities from businesses in your area. Get paid when SpotBounty operates or assigns the location.
              </p>
              <ul className="mt-10 space-y-5 max-w-md">
                {[
                  { icon: MapPin,     title: "Find real opportunities", body: "Warehouses, offices, gyms, factories in your ZIP." },
                  { icon: Zap,        title: "Submit in minutes",       body: "Guided wizard, autosave, no paperwork." },
                  { icon: DollarSign, title: "Get rewarded",            body: "Bounty tracked from submitted to earned to paid." },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <li key={b.title} className="flex gap-3">
                      <div className="w-9 h-9 rounded-lg border border-[#2A2F34] bg-[#FFC107]/5 flex items-center justify-center shrink-0">
                        <Icon size={15} className="text-[#FFC107]" />
                      </div>
                      <div>
                        <div className="text-[14px] font-black">{b.title}</div>
                        <div className="text-[12.5px] text-[#A1A1AA] mt-0.5">{b.body}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Right form panel */}
          <section className="flex items-center justify-center px-6 py-12 lg:py-16">
            <form onSubmit={submit} className="w-full max-w-md" data-testid="scout-login-form">
              <div className="sb-eyebrow">{mode === "signup" ? "New scout" : "Welcome back"}</div>
              <h2 className="mt-1 text-[28px] font-black">{mode === "signup" ? "Create your account" : "Sign in to your dashboard"}</h2>

              {/* Tab switcher */}
              <div className="mt-6 flex border-b border-[#2A2F34]">
                <button type="button" onClick={() => { setMode("signin"); setError(""); setInfo(""); }}
                        className={`flex-1 py-3 text-[12px] font-bold uppercase tracking-widest border-b-2 ${mode === "signin" ? "text-[#FFC107] border-[#FFC107]" : "text-[#A1A1AA] border-transparent"}`}
                        data-testid="scout-tab-signin">Log In</button>
                <button type="button" onClick={() => { setMode("signup"); setError(""); setInfo(""); }}
                        className={`flex-1 py-3 text-[12px] font-bold uppercase tracking-widest border-b-2 ${mode === "signup" ? "text-[#FFC107] border-[#FFC107]" : "text-[#A1A1AA] border-transparent"}`}
                        data-testid="scout-tab-signup">Create Account</button>
              </div>

              <div className="mt-6 space-y-4">
                {mode === "signup" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="sb-label">First Name</div>
                      <input required className="sb-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="scout-signup-first-name" autoComplete="given-name" />
                    </div>
                    <div>
                      <div className="sb-label">Last Name</div>
                      <input required className="sb-input" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="scout-signup-last-name" autoComplete="family-name" />
                    </div>
                  </div>
                )}
                <div>
                  <div className="sb-label">Email</div>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
                    <input required type="email" className="sb-input pl-9" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="scout-email" autoComplete="username" />
                  </div>
                </div>
                <div>
                  <div className="sb-label">Password</div>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
                    <input required type="password" minLength={8} className="sb-input pl-9" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="scout-password" autoComplete={mode === "signup" ? "new-password" : "current-password"} />
                  </div>
                </div>
                {mode === "signup" && (
                  <>
                    <div>
                      <div className="sb-label">Mobile Phone</div>
                      <input required type="tel" className="sb-input" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="scout-signup-phone" autoComplete="tel" placeholder="(555) 555-5555" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="sb-label">ZIP</div>
                        <input required className="sb-input" value={zip} onChange={(e) => setZip(e.target.value)} data-testid="scout-signup-zip" autoComplete="postal-code" />
                      </div>
                      <div>
                        <div className="sb-label">State</div>
                        <input required maxLength={2} className="sb-input uppercase" value={state} onChange={(e) => setState(e.target.value.toUpperCase())} data-testid="scout-signup-state" autoComplete="address-level1" placeholder="IL" />
                      </div>
                    </div>
                  </>
                )}

                {error && <div className="text-[12px] text-[#EF4444] flex items-center gap-2" data-testid="scout-login-error"><AlertCircle size={13} /> {error}</div>}
                {info && <div className="text-[12px] text-[#22C55E] flex items-center gap-2" data-testid="scout-login-info"><CheckCircle2 size={13} /> {info}</div>}

                <button type="submit" disabled={loading} data-testid="scout-login-submit" className="sb-btn-primary w-full text-[12px]" style={{ padding: "14px 16px" }}>
                  {loading ? <Loader2 size={14} className="animate-spin" /> : (mode === "signup" ? "Create Scout Account" : "Sign In")}
                </button>
                {awaitingVerification && (
                  <button type="button" onClick={resendVerification} className="w-full text-[12px] text-[#FFC107] hover:underline" data-testid="scout-resend-verification">
                    Resend verification email
                  </button>
                )}
                {mode === "signin" && (
                  <div className="flex items-center justify-between text-[12px] pt-1">
                    <button type="button" onClick={() => setShowFP(true)} className="text-[#FFC107] hover:underline" data-testid="scout-forgot-password">Forgot password?</button>
                    <button type="button" onClick={() => setShowFE(true)} className="text-[#A1A1AA] hover:text-white" data-testid="scout-forgot-email">Forgot email?</button>
                  </div>
                )}
                <div className="text-[11px] text-[#52525b] text-center pt-2 border-t border-[#2A2F34]">
                  By continuing you agree to SpotBounty&apos;s <Link to="/terms" className="hover:text-white">Terms</Link> and <Link to="/privacy" className="hover:text-white">Privacy</Link>.
                </div>
              </div>
            </form>
          </section>
        </div>
      </main>
      <ForgotPasswordModal open={showFP} onClose={() => setShowFP(false)} role="scout" />
      <ForgotEmailModal open={showFE} onClose={() => setShowFE(false)} defaultRole="scout" />
    </div>
  );
}
